/* eslint-disable no-dupe-keys */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useMemo, useEffect } from "react";
import {
  View,
  Text,
  FlatList,
  ScrollView,
  TouchableOpacity,
  TouchableWithoutFeedback,
  useWindowDimensions,
  Platform,
  ActivityIndicator,
} from "react-native";
import LHOverlay from "../../../../components/LHOverlay";
import LhButton from "../../../../components/LhButton";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import FontFamily from "../../../../constants/fonts";
import SkeletonRows from "./KfaTableSkeleton";
import { useTheme } from "../../../../theme/useTheme";
import { styles } from './KfaDataTable.styles';
import { getStatusStyle } from './kfaTableHelpers';
import KfaTablePagination from './KfaTablePagination';

// Types
type ColumnType = "toggle" | "checkbox";

type Column = {
  columnKey: string;
  header: string;
  type?: ColumnType;
  renderCell?: (item: any) => React.ReactNode;
  formatter?: (value: any) => string;
  flexGrow?: number; // Optional prop to define how much space a column should take relative to others
  width?: number; // Optional fixed override width (respected as minWidth in calculations)
  getSortValue?: (item: any) => any; // Custom sort value extractor for columns whose key differs from data field
};

type CheckboxField = "yesChecked" | "noChecked" | "naChecked";

type Props = {
  data: any[];
  columns: Column[];
  tableHeader: string;
  tableDescription: string;
  maxRecordsToShow?: number;
  onRowClick?: (rowData: any) => void;
  columnFlexValues?: { [columnKey: string]: number };
  showHeaderTopBorder?: boolean;
  expansionRenderer?: (item: any) => React.ReactNode;
  isLoading?: boolean;
  controlledPage?: number;
  controlledTotalPages?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  emptyMessage?: string;
  onClearFilters?: () => void;
};

const LHDataTable = ({
  data,
  columns,
  maxRecordsToShow = 10,
  onRowClick,
  columnFlexValues,
  showHeaderTopBorder = true,
  expansionRenderer,
  isLoading = false,
  controlledPage,
  controlledTotalPages,
  onPageChange: onExternalPageChange,
  onPageSizeChange: onExternalPageSizeChange,
  emptyMessage,
  onClearFilters,
}: Props) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayContent, setOverlayContent] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(maxRecordsToShow);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [columnWidths, setColumnWidths] = useState<{
    [key: string]: { minWidth: number; flex: number };
  }>({});
  const [layoutCalculated, setLayoutCalculated] = useState(false);

  const { width: screenWidth } = useWindowDimensions();

  // Helper to detect iOS mobile (phone) vs iPad/web
  const isIOSMobile = Platform.OS === "ios" && screenWidth < 768;

  // Enhanced estimateTextWidth function
  const estimateTextWidth = (text: string, isBold: boolean = false) => {
    const baseWidth = Platform.OS === "web" ? 8 : 6; // Average character width in pixels
    const boldMultiplier = isBold ? 1.2 : 1;

    // Count characters considering some might be wider
    let charCount = 0;
    const str = String(text);

    for (let i = 0; i < str.length; i++) {
      // Wider characters (like W, M)
      if (/[WM]/.test(str[i])) {
        charCount += 1.3;
      }
      // Narrow characters (like i, l, 1)
      else if (/[ilt1|]/.test(str[i])) {
        charCount += 0.6;
      }
      // Average characters
      else {
        charCount += 1;
      }
    }

    return charCount * baseWidth * boldMultiplier;
  };
  // Enhanced calculateColumnWidths
  const calculateColumnWidths = useMemo(() => {
    const widths: { [key: string]: { minWidth: number; flex: number } } = {};
    const padding = 24; // Increased horizontal padding to ensure text has enough room
    const minColumnWidth = 120; // Increased minimum width
    const maxColumnWidth = 300;

    // Ensure data is an array to prevent forEach errors
    const safeData = Array.isArray(data) ? data : [];

    columns.forEach((col) => {
      // Calculate header width
      let maxWidth = estimateTextWidth(col.header, true) + padding;

      // Calculate max content width
      safeData.forEach((item) => {
        let cellValue = item[col.columnKey];
        if (col.formatter && cellValue !== undefined && cellValue !== null) {
          cellValue = col.formatter(cellValue);
        }
        if (cellValue !== undefined && cellValue !== null) {
          const cellWidth = estimateTextWidth(String(cellValue)) + padding;
          maxWidth = Math.max(maxWidth, cellWidth);
        }
      });

      // If column has an explicit width override, use it directly
      if (col.width !== undefined) {
        maxWidth = col.width;
      } else {
        // Apply constraints from text estimation
        maxWidth = Math.max(minColumnWidth, Math.min(maxWidth, maxColumnWidth));
      }

      // Determine flex value
      // When an explicit col.width is set, use flex:0 so the column stays at
      // exactly that width and never absorbs extra space (prevents gap on web).
      let flex = 1;
      if (col.width !== undefined) {
        flex = 0;
      } else if (columnFlexValues?.[col.columnKey]) {
        flex = columnFlexValues[col.columnKey];
      } else if (col.flexGrow !== undefined) {
        flex = col.flexGrow;
      } else if (maxWidth > 200) {
        flex = 2;
      } else if (maxWidth > 150) {
        flex = 1.5;
      }

      widths[col.columnKey] = {
        minWidth: maxWidth,
        flex,
      };
    });

    return widths;
  }, [data, columns, screenWidth, columnFlexValues]);

  // Update column widths when dependencies change
  useEffect(() => {
    setColumnWidths(calculateColumnWidths);
  }, [calculateColumnWidths]);
  // Column-level filters removed; data is filtered externally
  const filteredData = useMemo(() => data, [data]);

  const sortedData = useMemo(() => {
    if (!sortConfig) {
      return filteredData;
    }
    return [...filteredData].sort((a, b) => {
      // Find the column object with the matching columnKey
      const column = columns.find((col) => col.columnKey === sortConfig.key);
      if (!column) {
        return 0;
      }

      const aValue = column.getSortValue ? column.getSortValue(a) : a[column.columnKey];
      const bValue = column.getSortValue ? column.getSortValue(b) : b[column.columnKey];

      // Handle null/undefined — push to bottom regardless of direction
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === "asc" ? 1 : -1;
      if (bValue == null) return sortConfig.direction === "asc" ? -1 : 1;

      // Numeric comparison
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      // Date instance comparison
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortConfig.direction === "asc"
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }

      // String comparison (case-insensitive)
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      if (aStr < bStr) return sortConfig.direction === "asc" ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig, columns]);

  const isServerSide = controlledPage !== undefined;
  const activePage = isServerSide ? controlledPage! : currentPage;
  const activeTotalPages = isServerSide ? (controlledTotalPages ?? 1) : Math.ceil(sortedData.length / rowsPerPage);
  const totalPages = activeTotalPages;
  const paginatedData = isServerSide
    ? sortedData
    : sortedData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
      );

  // Total width of all columns — used to keep horizontal scroll working even during loading/empty states
  const totalTableWidth = useMemo(() => {
    return columns.reduce((sum, col) => {
      const w = columnWidths[col.columnKey];
      return sum + (w ? w.minWidth : (col.width ?? 120));
    }, 0);
  }, [columns, columnWidths]);
  const handleSort = (columnKey: string) => {
    if (sortConfig?.key === columnKey) {
      setSortConfig({
        key: columnKey,
        direction: sortConfig.direction === "asc" ? "desc" : "asc",
      });
    } else {
      setSortConfig({ key: columnKey, direction: "asc" });
    }
  };

  const showOverlayWithCustomContent = (content: any) => {
    setOverlayContent(content);
    setOverlayVisible(true);
  };

  const closeOverlay = () => {
    setOverlayVisible(false);
    setOverlayContent(null);
  };

  // (column-level filters removed — search/filter handled externally)

  // Function to get status style based on status value
  const renderRow = ({ item, index }: { item: any; index: number }) => {
    const isSelected = selectedRowIndex === index;

    // Debug logging to see what we're working with
    if (index === 0) {
      console.log("First row item:", item);
      console.log(
        "Columns:",
        columns.map((col) => ({ key: col.columnKey, header: col.header }))
      );
    }

    const handleCellClick = (col: Column, cellValue: any) => {
      // Handle "type" column separately if needed
      if (col.columnKey === "type") {
        // Pass the raw item without any special flags
        if (onRowClick) {
          onRowClick({ ...item, isPulseCheckNavigation: true });
        }
        return;
      }

      // If the column key is "Title" or "Shift", handle differently - navigate to ShiftHandover
      if (col.columnKey === "Title" || col.columnKey === "Shift") {
        if (onRowClick) {
          onRowClick({
            ...item,
            isShiftHandOverNavigation: true,
            columnKey: col.columnKey,
          });
        }
        return;
      }

      // For all other columns and values, use the default behavior
      setSelectedRowIndex(index);

      // If there's a click handler, pass the item with the clicked column info
      if (onRowClick) {
        onRowClick({ ...item, columnKey: col.columnKey });
      } else {
        showOverlayWithCustomContent(item);
      }
    };

    const rowView = (
      <View
        style={[
          styles.row,
          styles.rowAutoHeight,
          isDark && { borderBottomColor: colors.border.default },
          isSelected && (isDark ? { backgroundColor: 'rgba(61,139,255,0.15)' } : styles.selectedRow),
        ]}
        testID="table-row"
        key={`row-${index}`}
      >
        {columns.map((col) => (
          <View
            key={col.columnKey}
            style={[
              styles.cellContainer,
              styles.cellContainerAutoHeight,
              {
                justifyContent: "flex-start", // Force left alignment for all cell containers
              },
              columnWidths[col.columnKey] && {
                minWidth: columnWidths[col.columnKey].minWidth,
                flex: columnWidths[col.columnKey].flex,
                // On web, omit fixed width so flex fills the container (prevents right-side gap)
                ...(Platform.OS !== "web" && { width: columnWidths[col.columnKey].minWidth }),
              },
            ]}
          >
            {col.renderCell ? (
              <React.Fragment key={`custom-cell-${col.columnKey}-${index}`}>
                {col.renderCell(item)}
              </React.Fragment>
            ) : col.columnKey === "action" &&
              item[col.columnKey] === "Resume" ? (
              <LhButton
                key={`action-button-${index}`}
                size="small"
                text={item[col.columnKey]}
                style={{ width: 100 }}
                onPress={() => console.log(`Action clicked for row ${index}`)}
              />
            ) : (
              <TouchableOpacity
                key={`cell-${col.columnKey}-${index}`}
                onPress={() => handleCellClick(col, item[col.columnKey])}
                style={[
                  styles.cellTouchable,
                  {
                    alignItems: "flex-start", // Force left alignment for all touchable cells
                    width: "100%",
                  },
                ]}
              >
                {(() => {
                  const colKeyLower = String(col.columnKey ?? "").toLowerCase();
                  const headerLower = String(col.header ?? "").toLowerCase();

                  // Treat only explicit Status/Action columns as the status pill.
                  const isStatusColumn =
                    colKeyLower === "status" ||
                    colKeyLower === "action" ||
                    headerLower === "status";

                  const cellValue = item[col.columnKey];

                  if (isStatusColumn) {
                    const rawText =
                      col.formatter && cellValue !== undefined && cellValue !== null
                        ? col.formatter(cellValue)
                        : cellValue;

                    // If value is null/undefined/empty, show plain dash like other columns
                    const displayText =
                      rawText === null ||
                      rawText === undefined ||
                      String(rawText).trim().length === 0
                        ? null
                        : String(rawText);

                    if (!displayText) {
                      return (
                        <Text
                          key={`text-${col.columnKey}-${index}`}
                          style={[
                            styles.cell,
                            styles.otherCellText,
                            { textAlign: "left" },
                            isDark && { color: colors.text.secondary },
                          ]}
                        >
                          -
                        </Text>
                      );
                    }

                    const statusStyle = getStatusStyle(displayText, isDark);

                    return (
                      <View
                        style={statusStyle.container}
                        onLayout={() =>
                          !layoutCalculated && setLayoutCalculated(true)
                        }
                      >
                        <Text
                          key={`text-${col.columnKey}-${index}`}
                          style={statusStyle.text}
                          numberOfLines={1}
                          adjustsFontSizeToFit={false}
                          ellipsizeMode="tail"
                        >
                          {displayText}
                        </Text>
                      </View>
                    );
                  }

                  return (
                    <Text
                      key={`text-${col.columnKey}-${index}`}
                      style={[
                        styles.cell,
                        {
                          textAlign: "left", // Force left alignment for all data cells
                        },
                        col.columnKey === "type" &&
                        col.columnKey !== columns[0].columnKey
                          ? styles.typeCell
                          : {},
                        col.columnKey === columns[0].columnKey
                          ? (isDark ? { color: colors.text.primary, fontWeight: '500' } : styles.firstCellText)
                          : (isDark ? { color: colors.text.secondary } : styles.otherCellText),
                      ]}
                      adjustsFontSizeToFit={false}
                    >
                      {col.formatter &&
                      cellValue !== undefined &&
                      cellValue !== null
                        ? col.formatter(cellValue)
                        : cellValue !== undefined && cellValue !== null
                          ? (typeof cellValue === 'boolean' ? (cellValue ? 'Yes' : 'No') : String(cellValue))
                          : "-"}
                    </Text>
                  );
                })()}
              </TouchableOpacity>
            )}
          </View>
        ))}
      </View>
    );

    // Render the optional expansion content immediately after the row so it appears inline
    const expansionNode = expansionRenderer ? expansionRenderer(item) : null;

    return (
      <React.Fragment key={`row-frag-${index}`}>
        {rowView}
        {expansionNode}
      </React.Fragment>
    );
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
    onExternalPageSizeChange?.(newRowsPerPage);
  };

  const renderPaginationControls = () => (
    <KfaTablePagination
      activePage={activePage}
      totalPages={totalPages}
      currentPage={currentPage}
      rowsPerPage={rowsPerPage}
      dropdownVisible={dropdownVisible}
      isServerSide={isServerSide}
      setDropdownVisible={setDropdownVisible}
      setCurrentPage={setCurrentPage}
      onExternalPageChange={onExternalPageChange}
      onRowsPerPageChange={handleRowsPerPageChange}
    />
  );

  // ── Mobile card row renderer ──────────────────────────────────────────────
  const renderMobileCard = ({ item, index }: { item: any; index: number }) => {
    const primaryCol = columns[0];
    const primaryValue = primaryCol
      ? (primaryCol.formatter ? primaryCol.formatter(item[primaryCol.columnKey]) : item[primaryCol.columnKey])
      : null;

    return (
      <TouchableOpacity
        key={`mobile-card-${index}`}
        onPress={() => onRowClick?.({ ...item })}
        activeOpacity={0.75}
        style={[
          styles.mobileCard,
          isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default },
        ]}
        testID="mobile-table-card"
      >
        {/* Primary field — shown as the card heading */}
        {primaryCol && (
          <Text
            style={[
              styles.mobileCardPrimary,
              isDark && { color: colors.brand.primary },
            ]}
            numberOfLines={3}
          >
            {primaryValue != null && String(primaryValue).trim() !== '' ? String(primaryValue) : '—'}
          </Text>
        )}

        {/* Remaining fields as label: value pairs */}
        {columns.slice(1).map((col) => {
          const rawValue = item[col.columnKey];
          const isStatusCol =
            String(col.columnKey).toLowerCase() === 'status' ||
            String(col.header).toLowerCase() === 'status';

          // Format the display value
          let displayText: string | null = null;
          if (col.formatter && rawValue !== undefined && rawValue !== null) {
            displayText = col.formatter(rawValue);
          } else if (rawValue !== undefined && rawValue !== null) {
            displayText = typeof rawValue === 'boolean' ? (rawValue ? 'Yes' : 'No') : String(rawValue);
          }

          return (
            <View key={col.columnKey} style={styles.mobileCardRow}>
              <Text
                style={[styles.mobileCardLabel, isDark && { color: colors.text.secondary }]}
                numberOfLines={1}
              >
                {col.header}
              </Text>
              {col.renderCell ? (
                <View style={styles.mobileCardValue}>
                  {col.renderCell(item)}
                </View>
              ) : isStatusCol && displayText ? (
                <View style={[styles.mobileCardValue, { alignItems: 'flex-start' }]}>
                  <View style={getStatusStyle(displayText, isDark).container}>
                    <Text style={getStatusStyle(displayText, isDark).text} numberOfLines={1}>
                      {displayText}
                    </Text>
                  </View>
                </View>
              ) : (
                <Text
                  style={[styles.mobileCardValue, isDark && { color: colors.text.primary }]}
                  numberOfLines={3}
                >
                  {displayText != null && String(displayText).trim() !== '' ? displayText : '—'}
                </Text>
              )}
            </View>
          );
        })}
      </TouchableOpacity>
    );
  };

  return (
    <TouchableWithoutFeedback onPress={() => {
      setDropdownVisible(false);
    }}>
      <View style={styles.container}>
        {isIOSMobile ? (
          // ── iPhone: card-per-row layout ───────────────────────────────────
          <>
            {isLoading ? (
              <SkeletonRows
                columns={columns}
                columnWidths={columnWidths}
                rowCount={5}
              />
            ) : paginatedData.length > 0 ? (
              <FlatList
                data={paginatedData}
                renderItem={renderMobileCard}
                keyExtractor={(_, index) => index.toString()}
                showsVerticalScrollIndicator={false}
                scrollEnabled={false}
                nestedScrollEnabled={true}
                ItemSeparatorComponent={() => (
                  <View style={[styles.mobileCardSeparator, isDark && { backgroundColor: colors.border.divider }]} />
                )}
              />
            ) : (
              <View style={styles.noDataContainer}>
                <Text style={[styles.noDataText, isDark && { color: colors.text.secondary }]}>
                  {emptyMessage ?? 'No data to display'}
                </Text>
                {onClearFilters && (
                  <TouchableOpacity onPress={onClearFilters} style={{ marginTop: 8 }}>
                    <Text style={styles.clearFiltersLink}>Clear All Filters</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
            {!isLoading && sortedData.length > 0 && renderPaginationControls()}
          </>
        ) : (
          // ── iPad / Web: horizontal-scroll table ───────────────────────────
          <>
        <View style={{ minHeight: 600, position: 'relative' }}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={true}
            style={{ flexGrow: 1 }}
            contentContainerStyle={{ minWidth: totalTableWidth }}
            scrollEventThrottle={16}
            nestedScrollEnabled={true}
          >
          <View style={{ width: totalTableWidth, minWidth: totalTableWidth, overflow: 'visible' }}>
            <View
              style={[
                styles.headerRow,
                isDark && { borderBottomColor: colors.border.default },
                showHeaderTopBorder
                  ? { borderTopWidth: 2, borderTopColor: isDark ? colors.border.default : '#D7E5F8' }
                  : { borderTopWidth: 0, borderTopColor: 'transparent' },
              ]}
            >
              {columns.map((col) => {
                const isSorted = sortConfig?.key === col.columnKey;

                return (
                  <View
                    key={col.columnKey}
                    style={[
                      styles.cellContainer,
                      styles.headerCellContainer,
                      {
                        justifyContent: "flex-start",
                        position: 'relative',
                        overflow: 'visible',
                        height: 'auto' as any,
                        minHeight: 48,
                        alignItems: 'flex-start',
                      },
                      columnWidths[col.columnKey] && {
                        minWidth: columnWidths[col.columnKey].minWidth,
                        flex: columnWidths[col.columnKey].flex,
                        ...(Platform.OS !== "web" && { width: columnWidths[col.columnKey].minWidth }),
                      },
                    ]}>
                    <TouchableOpacity
                      onPress={() => handleSort(col.columnKey)}
                      style={{ flex: 1 }}
                    >
                      <View style={styles.headerCellContent}>
                        <Text
                          style={[
                            styles.headerCellText,
                            isDark && { color: colors.text.secondary },
                          ]}
                        >
                          {col.header}
                        </Text>
                        <View style={styles.sortIconContainer}>
                          {!isSorted ? (
                            <MaterialIcons
                              name="unfold-more"
                              size={16}
                              color={isDark ? colors.text.secondary : '#616161'}
                              style={styles.sortIcon}
                            />
                          ) : sortConfig?.direction === "asc" ? (
                            <MaterialIcons
                              name="keyboard-arrow-up"
                              size={16}
                              color="#616161"
                              style={styles.sortIcon}
                            />
                          ) : (
                            <MaterialIcons
                              name="keyboard-arrow-down"
                              size={16}
                              color="#616161"
                              style={styles.sortIcon}
                            />
                          )}
                        </View>
                      </View>
                    </TouchableOpacity>
                  </View>
                );
              })}
            </View>
            {isLoading ? (
              <SkeletonRows
                columns={columns}
                columnWidths={columnWidths}
                rowCount={10}
              />
            ) : paginatedData.length > 0 ? (
              <View
                style={[
                  Platform.OS === "ios" || Platform.OS === "android"
                    ? { flex: 1, maxHeight: 800, minHeight: 500 }
                    : { flex: 1, minHeight: 500 },
                ]}
              >
                <FlatList
                  data={paginatedData}
                  renderItem={renderRow}
                  keyExtractor={(_, index) => index.toString()}
                  showsVerticalScrollIndicator={true}
                  scrollEnabled={
                    Platform.OS === "ios" || Platform.OS === "android"
                  }
                  style={{ flexGrow: 1 }}
                  nestedScrollEnabled={true}
                  scrollEventThrottle={16}
                />
              </View>
            ) : (
              <View style={styles.noDataContainer}>
                <Text style={styles.noDataText}>
                  {emptyMessage ?? 'No data to display'}
                </Text>
                {onClearFilters && (
                  <TouchableOpacity onPress={onClearFilters} style={{ marginTop: 8 }}>
                    <Text style={styles.clearFiltersLink}>Clear All Filters</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
          </View>
        </ScrollView>
        </View>
        {!isLoading && sortedData.length > 0 && renderPaginationControls()}
          </>
        )}
        <LHOverlay
          visible={overlayVisible}
          onDismiss={closeOverlay}
          title={
            overlayContent ? `${overlayContent.type} Details` : "Row Details"
          }
          size="large"
        >
          {overlayContent && (
            <View style={styles.overlayContent}>
              <View style={styles.overlaySection}>
                <Text style={styles.overlaySectionTitle}>Summary</Text>
                <View
                  style={[
                    styles.overlaySummary,
                    Platform.OS !== "web" && styles.overlaySummaryMobile,
                  ]}
                >
                  {columns.map((col) => (
                    <View
                      key={col.columnKey}
                      style={[
                        styles.overlayField,
                        Platform.OS !== "web" && styles.overlayFieldMobile,
                      ]}
                    >
                      <Text style={styles.overlayFieldLabel}>
                        {col.header}:
                      </Text>
                      <Text style={styles.overlayFieldValue}>
                        {String(overlayContent[col.columnKey])}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>

              {overlayContent.observationNotes && (
                <View style={styles.overlaySection}>
                  <Text style={styles.overlaySectionTitle}>
                    Observation Notes
                  </Text>
                  <Text style={styles.overlayAdditionalContent}>
                    {overlayContent.observationNotes}
                  </Text>
                </View>
              )}

              {overlayContent.miscellaneousObservations && (
                <View style={styles.overlaySection}>
                  <Text style={styles.overlaySectionTitle}>
                    Miscellaneous Observations
                  </Text>
                  <Text style={styles.overlayAdditionalContent}>
                    {overlayContent.miscellaneousObservations}
                  </Text>
                </View>
              )}

              <View style={styles.overlaySection}>
                <Text style={styles.overlaySectionTitle}>
                  Additional Information
                </Text>
                <View
                  style={[
                    styles.overlaySummary,
                    Platform.OS !== "web" && styles.overlaySummaryMobile,
                  ]}
                >
                  {Object.entries(overlayContent)
                    .filter(
                      ([key]) =>
                        !columns.some((col) => col.columnKey === key) &&
                        key !== "observationNotes" &&
                        key !== "miscellaneousObservations"
                    )
                    .map(([key, value]) => (
                      <View
                        key={key}
                        style={[
                          styles.overlayField,
                          Platform.OS !== "web" && styles.overlayFieldMobile,
                        ]}
                      >
                        <Text style={styles.overlayFieldLabel}>
                          {key.charAt(0).toUpperCase() +
                            key.slice(1).replace(/([A-Z])/g, " $1")}
                          :
                        </Text>
                        <Text style={styles.overlayFieldValue}>
                          {typeof value === "boolean"
                            ? value
                              ? "Yes"
                              : "No"
                            : String(value)}
                        </Text>
                      </View>
                    ))}
                </View>
              </View>
            </View>
          )}
        </LHOverlay>
      </View>
    </TouchableWithoutFeedback>
  );
};

export default LHDataTable;

